import { useAtom } from "jotai";
import { Button, Flex, Modal, Textarea } from "src/elements";
import { merchflowNotesModalAtom, type MerchflowNotesModalData } from "./store/atoms";
import { useEffect, useState } from "react";
import { useModals } from "src/components/Modals";
import { ConfirmModalProps } from "../ModalConfirm/store/types";
import { useUpdateMerchflowMutation } from "src/api/merchflows/updateMerchflow";

export const MerchflowNotesModal = () => {
  const [data, setData] = useAtom(merchflowNotesModalAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  return data ? <MerchflowNotesModalInner {...data} /> : null;
};

const MerchflowNotesModalInner = ({
  merchflowId,
  notes: _notes,
  onSave,
}: MerchflowNotesModalData) => {
  const { closeModal: _closeModal, openConfirmModal } = useModals();
  const updateMerchflow = useUpdateMerchflowMutation();

  const [notes, setNotes] = useState(_notes);
  const [isModified, setIsModified] = useState(false);

  const closeModalSafe = () => {
    if (isModified) {
      openConfirmModal(getConfirmationModalProps());
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    _closeModal("MerchflowNotesModal");
  };

  const saveNotes = async () => {
    try {
      const response = await updateMerchflow.mutateAsync({
        merchflowId,
        payload: { notes },
      });
      if (onSave) onSave(notes, response);
      closeModal();
    } catch {
      return;
    }
  };

  const getConfirmationModalProps = () => {
    return {
      title: "Discard Unsaved Changes?",
      description:
        "Are you sure you want to discard your changes? All unsaved notes will be permanently lost.",
      buttons: [
        {
          id: "continue-editing",
          label: "Continue Editing",
          color: "primary",
          variant: "inverted",
        },
        {
          id: "discard-changes",
          label: "Discard Changes",
          color: "red",
          variant: "default",
          onClick: closeModal,
        },
      ],
    } satisfies ConfirmModalProps;
  };

  return (
    <Modal
      name="MerchflowNotesModal"
      title={`Merchflow ${merchflowId} notes`}
      width="500px"
      confirmModal={isModified && getConfirmationModalProps()}
    >
      <Flex column padding="10px" gap="10px">
        <Textarea
          title="Notes"
          value={notes}
          setValue={(notes) => {
            setNotes(notes);
            setIsModified(true);
          }}
          height="200px"
        />

        <Flex gap="10px">
          <Button
            color={isModified ? "red" : "primary"}
            variant="inverted"
            width="50%"
            onClick={closeModalSafe}
          >
            {isModified ? "Cancel" : "Back"}
          </Button>

          <Button
            width="50%"
            onClick={saveNotes}
            isEnabled={isModified}
            isLoading={updateMerchflow.isPending}
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
